import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Checkbox, Form, Select } from 'antd';
import { SelectProps } from 'antd/es/select';
import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PeriodArray } from '../common/types';
import { BooleanRadio } from '../datasubject';
import translations from './translations/DataObjectFilter.translations.json';
import {
  DatePicker,
  DirectoryFilter,
  DirectoryFilterProps,
  FieldProps,
  Select as PagedSelect,
} from '../common/components';
import {
  DataObjectFilterDto,
  DataObjectGroupType,
  DataObjectOutputType,
  DataObjectType,
  DataObjectView,
} from './types';
import { DATE_FORMAT, getDayjs } from '../common/utils';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import { filterOption } from '../common/form';
import { useSystemClassifierSelectOptions } from '../configuration';
import { DataObjectDescriptionAutoComplete } from '../common/components/DataObjectDescriptionAutoComplete';
import { AccessGroupAutoComplete } from '../accessGroup';
import {
  BaseDataObjectGroupDto,
  expandDataObjectGroupFlatValues,
  getDataObjectGroupFlatOptions,
  getDataObjectGroupsFromOptionPages,
  getSelectedIdsFromFlatValues,
  useInfiniteSearchDataObjectGroupTreesQuery,
} from '../dataObjectGroup';
import { useInfiniteSearchSelect } from '../common/form';

const { RangePicker } = DatePicker;

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-objects-filter',
);
addTranslations(translations);

export interface DataObjectFilterProps extends DirectoryFilterProps {
  dataObjectView: DataObjectView;
  filters: DataObjectFilterDto;
  formRef: RefObject<FormInstance | null>;
}

export const DataObjectFilter: FC<Omit<DataObjectFilterProps, 'fields'>> = (
  props,
) => {
  const {
    dataObjectView: { dataObjectType, dataObjectGroupType },
    filters,
    formRef,
    onFilter,
  } = props;
  const [initialFilterValues] = useState(() => initFilterValues(filters));
  const { t } = useTranslation();

  const systemClassifiers = useDataObjectClassifiers();
  const [dataObjectGroups, setDataObjectGroups] = useState<
    BaseDataObjectGroupDto[]
  >([]);

  const handleFilter = useCallback(
    (values: Record<string, any>) =>
      onFilter({
        ...values,
        groupAndSubgroupIds: expandDataObjectGroupFlatValues(
          values.groupAndSubgroupIds,
          dataObjectGroups,
        ),
      }),
    [dataObjectGroups, onFilter],
  );

  const outputTypeCodes = Form.useWatch(
    'outputTypeCodes',
    formRef.current || undefined,
  );
  const hasIntermediateOutputType = outputTypeCodes?.includes(
    DataObjectOutputType.INTERMEDIATE,
  );

  useEffect(() => {
    if (
      dataObjectType === DataObjectType.OUT &&
      outputTypeCodes &&
      !hasIntermediateOutputType
    ) {
      // Reset primaryOutputIds filter if INTERMEDIATE output type is not selected
      formRef.current?.setFieldValue('primaryOutputIds', undefined);
    }
  }, [dataObjectType, formRef, hasIntermediateOutputType, outputTypeCodes]);

  const { statusCodes } = filters;

  const filterFields = useMemo(() => {
    const fields: FieldProps<Record<string, any>>[] = [];
    const outputsField = {
      name: 'outputs',
      label: t('field.outputs.label'),
      initialValue: initialFilterValues.outputs,
      input: () => (
        <DataObjectDescriptionAutoComplete
          id="outputs-filter"
          placeholder={t('field.defaults.placeholder')}
          dataObjectType={DataObjectType.OUT}
          defaultOption={initialFilterValues.outputs}
          styles={{
            popup: { root: { width: 'max-content' } },
          }}
        />
      ),
    };

    if (DataObjectType.OUT === dataObjectType) {
      fields.push({
        name: 'outputTypeCodes',
        label: '',
        initialValue: initialFilterValues.outputTypeCodes,
        input: () => (
          <Checkbox.Group>
            <Checkbox
              id="outputTypeCode-OUTPUT"
              value={DataObjectOutputType.OUTPUT}
            >
              {t('field.dataObjectTypes.OUTPUT')}
            </Checkbox>
            <Checkbox
              id="outputTypeCode-INTERMEDIATE"
              value={DataObjectOutputType.INTERMEDIATE}
            >
              {t('field.dataObjectTypes.INTERMEDIATE')}
            </Checkbox>
            <Checkbox
              id="outputTypeCode-REPORT"
              value={DataObjectOutputType.REPORT}
            >
              {t('field.dataObjectTypes.REPORT')}
            </Checkbox>
          </Checkbox.Group>
        ),
      });
    }

    if (
      DataObjectType.OUT === dataObjectType &&
      (!outputTypeCodes?.length ||
        outputTypeCodes?.includes(DataObjectOutputType.REPORT))
    ) {
      fields.push(
        {
          name: 'recipientIds',
          label: t(`field.recipient.label`),
          input: () => (
            <Select
              id="recipientIds-filter"
              placeholder={t('field.defaults.placeholder')}
              mode="multiple"
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              options={systemClassifiers.REPORT_RECIPIENT}
              filterOption={filterOption}
              defaultValue={initialFilterValues.recipientIds}
            />
          ),
        },
        {
          name: 'confidentialityStatusIds',
          label: t(`field.confidentialityStatus.label`),
          input: () => (
            <Select
              id="confidentialityStatusIds-filter"
              placeholder={t('field.defaults.placeholder')}
              mode="multiple"
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              options={systemClassifiers.CONFIDENTIALITY_STATUS}
              filterOption={filterOption}
              defaultValue={initialFilterValues.confidentialityStatusIds}
            />
          ),
        },
        {
          name: 'indicatorStatus',
          label: t(`field.indicatorStatus.label`),
          input: () => (
            <BooleanRadio
              id="indicatorStatus-all"
              defaultValue={initialFilterValues.indicatorStatus}
            />
          ),
        },
      );
    }

    if (DataObjectType.OUT === dataObjectType) {
      fields.push(
        {
          name: 'sourceIds',
          label: t('field.sourceData.label'),
          initialValue: initialFilterValues.sourceIds,
          input: () => (
            <DataObjectDescriptionAutoComplete
              id="sourceIds-filter"
              placeholder={t('field.defaults.placeholder')}
              excludedOutputTypeCodes={[DataObjectOutputType.REPORT]}
              defaultOption={initialFilterValues.sourceIds}
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
            />
          ),
        },
        outputsField,
      );

      if (hasIntermediateOutputType) {
        fields.push({
          name: 'primaryOutputIds',
          label: t('field.primaryOutputs.label'),
          initialValue: initialFilterValues.primaryOutputIds,
          input: () => (
            <DataObjectDescriptionAutoComplete
              id="primaryOutputIds-filter"
              placeholder={t('field.defaults.placeholder')}
              dataObjectType={DataObjectType.OUT}
              defaultOption={initialFilterValues.primaryOutputIds}
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
            />
          ),
        });
      }
    }

    fields.push(
      {
        name: 'groupAndSubgroupIds',
        label: t(`field.groupAndSubgroup.` + dataObjectType),
        initialValue: initialFilterValues.groupSubgroupIds,
        input: () => (
          <DataObjectGroupFilterSelect
            id="groupAndSubgroupIds-filter"
            placeholder={t('field.defaults.placeholder')}
            mode="multiple"
            groupType={dataObjectGroupType}
            onGroupsChange={setDataObjectGroups}
            styles={{
              popup: { root: { width: 'max-content' } },
            }}
          />
        ),
      },
      {
        name: 'domainIds',
        label: t(`field.domain.label`),
        input: () => (
          <Select
            id="domainIds-filter"
            placeholder={t('field.defaults.placeholder')}
            mode="multiple"
            styles={{
              popup: { root: { width: 'max-content' } },
            }}
            options={systemClassifiers.DOMAIN}
            filterOption={filterOption}
            defaultValue={initialFilterValues.domainIds}
          />
        ),
      },
      {
        name: 'frequencyIds',
        label: t(`field.frequency.label`),
        input: () => (
          <Select
            id="frequencyIds-filter"
            placeholder={t('field.defaults.placeholder')}
            mode="multiple"
            styles={{
              popup: { root: { width: 'max-content' } },
            }}
            options={systemClassifiers.FREQUENCY}
            filterOption={filterOption}
            defaultValue={initialFilterValues.frequencyIds}
          />
        ),
      },
    );

    if (DataObjectType.IN === dataObjectType) {
      fields.push(
        {
          name: 'originIds',
          label: t(`field.source.label`),
          input: () => (
            <Select
              id="originIds-filter"
              placeholder={t('field.defaults.placeholder')}
              mode="multiple"
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              options={systemClassifiers.DATA_ORIGIN}
              filterOption={filterOption}
              defaultValue={initialFilterValues.originIds}
            />
          ),
        },
        {
          name: 'dataProviderGroupIds',
          label: t(`field.dataProviderGroup.label`),
          input: () => (
            <Select
              id="dataProviderGroupIds-filter"
              placeholder={t('field.defaults.placeholder')}
              mode="multiple"
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              options={systemClassifiers.DATA_PROVIDER_GROUP}
              filterOption={filterOption}
              defaultValue={initialFilterValues.dataProviderGroupIds}
            />
          ),
        },
      );
    }

    fields.push({
      name: 'accessGroupIds',
      label: t(`field.accessGroup.label`),
      initialValue: initialFilterValues.accessGroupIds,
      input: () => (
        <AccessGroupAutoComplete
          id="accessGroupIds-filter"
          placeholder={t('field.defaults.placeholder')}
          mode="multiple"
          styles={{
            popup: { root: { width: 'max-content' } },
          }}
        />
      ),
    });

    fields.push({
      name: 'statusCodes',
      label: t(`field.state.label`),
      initialValue: statusCodes,
      input: () => (
        <Select
          id="statusCodes-filter"
          placeholder={t('field.defaults.placeholder')}
          mode="multiple"
          styles={{
            popup: { root: { width: 'max-content' } },
          }}
          options={systemClassifiers.DATA_OBJ_GRP_VER_STATUS}
          filterOption={filterOption}
          defaultValue={initialFilterValues.statusCodes}
        />
      ),
    });

    fields.push({
      name: 'validPeriod',
      label: t(`field.validPeriod.label`),
      input: () => (
        <RangePicker
          id="validPeriod-filter"
          allowEmpty={[true, true]}
          format={DATE_FORMAT}
          defaultValue={initialFilterValues.validPeriod}
        />
      ),
    });

    if (DataObjectType.IN === dataObjectType) {
      fields.push(outputsField);
    }

    return fields;
  }, [
    dataObjectType,
    dataObjectGroupType,
    initialFilterValues,
    outputTypeCodes,
    hasIntermediateOutputType,
    statusCodes,
    systemClassifiers,
    t,
  ]);

  return (
    <DirectoryFilter
      fields={filterFields}
      {...props}
      ref={formRef}
      onFilter={handleFilter}
    />
  );
};

const DataObjectGroupFilterSelect: FC<
  SelectProps<string[]> & {
    groupType: DataObjectGroupType;
    onGroupsChange: (groups: BaseDataObjectGroupDto[]) => void;
  }
> = ({ groupType, onGroupsChange, value, ...props }) => {
  const selectedIds = getSelectedIdsFromFlatValues(value);
  const { data, selectProps } = useInfiniteSearchSelect({
    useSearchHook: useInfiniteSearchDataObjectGroupTreesQuery,
    searchOptions: { groupType, ...selectedIds },
  });
  const groups = useMemo(
    () => getDataObjectGroupsFromOptionPages(data?.pages),
    [data?.pages],
  );
  const options = useMemo(
    () => getDataObjectGroupFlatOptions(groups),
    [groups],
  );

  useEffect(() => onGroupsChange(groups), [groups, onGroupsChange]);

  return (
    <PagedSelect {...props} {...selectProps} value={value} options={options} />
  );
};

function initFilterValues(filters: DataObjectFilterDto) {
  const groupSubgroupIds = initGroupSubgroup(filters);
  const validPeriod: PeriodArray | undefined =
    filters.validFrom || filters.validTo
      ? [
          filters.validFrom ? getDayjs(filters.validFrom) : null,
          filters.validTo ? getDayjs(filters.validTo) : null,
        ]
      : undefined;

  return {
    ...filters,
    groupSubgroupIds,
    validPeriod,
  };
}

function initGroupSubgroup(filters: DataObjectFilterDto) {
  return [
    ...(filters.groupIds?.map((v) => `g-${v}`) ?? []),
    ...(filters.subgroupIds?.map((v) => `s-${v}`) ?? []),
  ];
}

function useDataObjectClassifiers() {
  const optionsById = useSystemClassifierSelectOptions([
    'REPORT_RECIPIENT',
    'CONFIDENTIALITY_STATUS',
    'DOMAIN',
    'FREQUENCY',
    'DATA_ORIGIN',
    'DATA_PROVIDER_GROUP',
  ]);
  const optionsByCode = useSystemClassifierSelectOptions(
    ['DATA_OBJ_GRP_VER_STATUS'],
    'code',
  );

  return useMemo(
    () => ({
      ...optionsById,
      ...optionsByCode,
    }),
    [optionsById, optionsByCode],
  );
}
